/**
 * Web implementation of eventsAPI.
 *
 * Each method proxies through the server at /api/proxy/events/<upstream-path>,
 * which forwards with the user's auth cookie injected as Bearer token.
 * The response shape matches buildApiResult from EventsApiService exactly.
 */

const BASE = '/api/proxy/events';

async function req(
  method: string,
  path: string,
  body?: unknown,
  query?: Record<string, string | number | boolean | undefined>,
): Promise<unknown> {
  const qs = query
    ? Object.entries(query)
        .filter(([, v]) => v !== undefined && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v!))}`)
        .join('&')
    : '';

  const url = `${BASE}${path}${qs ? `?${qs}` : ''}`;

  const res = await fetch(url, {
    method,
    credentials: 'include',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  let data: unknown;
  try { data = JSON.parse(text); } catch { data = text; }

  const ok = res.status >= 200 && res.status < 300;
  return { ok, status: res.status, data, error: ok ? undefined : `HTTP ${res.status}` };
}

const get = (path: string, q?: Record<string, string | number | boolean | undefined>) =>
  req('GET', path, undefined, q);
const post = (path: string, body: unknown) => req('POST', path, body);
const del = (path: string, q?: Record<string, string | number | boolean | undefined>) =>
  req('DELETE', path, undefined, q);

// World Triathlon calls don't go through the generic Events API proxy above -- they hit
// a dedicated server route (src/server/routes/worldTriathlon.ts) that forwards to
// triathlon.org's external API using a server-held key, mirroring what Electron's main
// process does directly. Same request/response shape as req(), different base path.
async function worldTriathlonGet(
  path: string,
  query?: Record<string, string | number | boolean | undefined>,
): Promise<unknown> {
  const qs = query
    ? Object.entries(query)
        .filter(([, v]) => v !== undefined && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v!))}`)
        .join('&')
    : '';
  const url = `/api/world-triathlon${path}${qs ? `?${qs}` : ''}`;

  const res = await fetch(url, { method: 'GET', credentials: 'include' });
  const text = await res.text();
  let data: unknown;
  try { data = JSON.parse(text); } catch { data = text; }

  const ok = res.status >= 200 && res.status < 300;
  return { ok, status: res.status, data, error: ok ? undefined : `HTTP ${res.status}` };
}

const e = encodeURIComponent;

/**
 * There's no backend route that returns raw CSV text directly -- only a "give me
 * a presigned S3 download URL" route. Fetch that first, then fetch the presigned
 * URL itself (no auth needed, it's presigned), matching EventsApiService's
 * getRaceCsvFileText on the Electron side.
 */
async function fetchCsvViaDownloadUrl(
  downloadUrlPath: string,
  options: { raceId: string; expiresIn?: number },
  defaultFileName: string,
): Promise<{ ok: boolean; status?: number; data?: unknown; error?: string }> {
  const metaRes = (await get(downloadUrlPath, options as Record<string, string | number>)) as {
    ok: boolean;
    status?: number;
    data?: { url?: string; fileName?: string; contentType?: string };
    error?: string;
  };

  if (!metaRes.ok || !metaRes.data?.url) {
    return { ok: false, status: metaRes.status ?? 500, error: metaRes.error || 'Failed to get download URL' };
  }

  const res = await fetch(metaRes.data.url);
  if (!res.ok) {
    return { ok: false, status: res.status, error: `HTTP ${res.status}` };
  }

  const csvText = await res.text();
  return {
    ok: true,
    status: 200,
    data: {
      fileName: metaRes.data.fileName || defaultFileName,
      contentType: metaRes.data.contentType || 'text/csv',
      csvText,
    },
  };
}

export function createWebEventsApi() {
  return {
    getHealth: () => get('/health'),

    createEvent: (payload: Record<string, unknown>) => post('/events', payload),

    listWorldTriathlonEvents: (params: Record<string, unknown> = {}) =>
      worldTriathlonGet('/events', params as Record<string, string>),

    listWorldTriathlonPrograms: (eventId: string) =>
      worldTriathlonGet(`/events/${e(eventId)}/programs`),

    listMyEvents: () => get('/my/events'),

    getFirmwareVersions: (role?: string) => get('/firmware/versions', { role }),

    getModemFirmwareVersions: () => get('/modem-firmware/versions'),

    uploadFirmware: (_params: unknown) =>
      Promise.resolve({ ok: false, error: 'Use the file upload form (web mode)' }),

    getDeviceMappings: () => get('/devices/mappings'),

    putDeviceMappings: (devices: Record<string, string>[]) =>
      post('/devices/mappings', { devices }),

    queryDeviceTls: (deviceIds: string[]) => post('/devices/tls/query', { deviceIds }),

    getDeviceLifecycle: (params: { deviceId?: string; deviceIds?: string[] } = {}) =>
      params.deviceId
        ? get('/devices/lifecycle', { deviceId: params.deviceId })
        : post('/devices/lifecycle', params),

    setDeviceLifecycle: (params: Record<string, unknown>) => post('/devices/lifecycle', params),

    getDeviceAttributes: (params: { deviceId?: string; deviceIds?: string[] } = {}) =>
      params.deviceId
        ? get('/devices/attributes', { deviceId: params.deviceId })
        : post('/devices/attributes', params),

    setDeviceAttributes: (params: Record<string, unknown>) => post('/devices/attributes', params),

    scanDataExplorerTable: (params: {
      tableName: string;
      limit?: number;
      nextToken?: string;
    }) =>
      get('/data-explorer/table', {
        tableName: params.tableName,
        limit: params.limit,
        nextToken: params.nextToken,
      }),

    listRequests: () => get('/requests'),

    listEventRequests: (eventId: string) => get(`/events/${e(eventId)}/requests`),

    getRequestCommands: (requestId: string) => get(`/requests/${e(requestId)}/commands`),

    getEventRequestCommands: (eventId: string, requestId: string) =>
      get(`/events/${e(eventId)}/requests/${e(requestId)}/commands`),

    listEvents: (options: { limit?: number; nextToken?: string } = {}) =>
      get('/events', { limit: options.limit, nextToken: options.nextToken }),

    getDeviceStatus: (deviceId: string) => get(`/devices/${e(deviceId)}/status`),

    getDeviceLatestCommand: (deviceId: string) => get(`/devices/${e(deviceId)}/command`),

    queryDeviceStatuses: (deviceIds: string[]) => post('/devices/statuses/query', { deviceIds }),

    queryDeviceLatestCommands: (deviceIds: string[]) =>
      post('/devices/commands/query', { deviceIds }),

    queryDeviceEventAssignments: (deviceIds: string[]) =>
      post('/devices/event-assignments/query', { deviceIds }),

    queryDeviceEventAssignmentHistory: (deviceIds: string[]) =>
      post('/devices/event-assignments/history/query', { deviceIds }),

    assignDevices: (eventId: string, params: { deviceIds: string[]; raceId: string }) =>
      post(`/events/${e(eventId)}/devices`, params),

    assignOperators: (
      eventId: string,
      operators: { userId: string; roleInEvent?: string }[],
    ) => post(`/events/${e(eventId)}/operators`, { operators }),

    listEventDevices: (
      eventId: string,
      options: { limit?: number; nextToken?: string; raceId?: string } = {},
    ) => get(`/events/${e(eventId)}/devices`, options as Record<string, string>),

    listRaceAthletes: (
      eventId: string,
      raceId: string,
      options: { stage?: string } = {},
    ) =>
      get(`/events/${e(eventId)}/races/${e(raceId)}/athletes`, options as Record<string, string>),

    markRaceAthletesDns: (
      eventId: string,
      raceId: string,
      payload: { athletes: { athleteId: string; stage: string; reason?: string }[] },
    ) => post(`/events/${e(eventId)}/races/${e(raceId)}/athletes/dns`, payload),

    updateRaceAthleteAssignment: (
      eventId: string,
      raceId: string,
      payload: Record<string, unknown>,
    ) => post(`/events/${e(eventId)}/races/${e(raceId)}/athletes/assignment`, payload),

    addRaceAthleteWithDevices: (
      eventId: string,
      raceId: string,
      payload: {
        athleteName: string;
        athleteRaceNo: string;
        rearDeviceId: string;
        frontDeviceId?: string;
        athleteId?: string;
      },
    ) => post(`/events/${e(eventId)}/races/${e(raceId)}/athletes/add`, payload),

    getEventDeviceStatus: (eventId: string, deviceId: string) =>
      get(`/events/${e(eventId)}/devices/${e(deviceId)}/status`),

    getEventDeviceLatestCommand: (eventId: string, deviceId: string) =>
      get(`/events/${e(eventId)}/devices/${e(deviceId)}/command`),

    queryEventDeviceStatuses: (eventId: string, deviceIds: string[]) =>
      post(`/events/${e(eventId)}/devices/statuses/query`, { deviceIds }),

    queryEventDeviceLatestCommands: (eventId: string, deviceIds: string[]) =>
      post(`/events/${e(eventId)}/devices/commands/query`, { deviceIds }),

    queryEventDeviceCommandHistory: (eventId: string, deviceIds: string[]) =>
      post(`/events/${e(eventId)}/devices/commands/history/query`, { deviceIds }),

    submitAthletesCsv: (
      eventId: string,
      payload: { raceId: string; csvBase64?: string; csvText?: string; fileName?: string; contentType?: string },
    ) => post(`/events/${e(eventId)}/athletes`, payload),

    getAthletesCsvMeta: (eventId: string, options: { raceId: string }) =>
      get(`/events/${e(eventId)}/athletes`, { raceId: options.raceId }),

    getAthletesCsvDownloadUrl: (
      eventId: string,
      options: { raceId: string; expiresIn?: number },
    ) => get(`/events/${e(eventId)}/athletes/download`, options as Record<string, string | number>),

    getAthletesCsv: (eventId: string, options: { raceId: string; expiresIn?: number }) =>
      fetchCsvViaDownloadUrl(`/events/${e(eventId)}/athletes/download`, options, 'athletes.csv'),

    submitDevicePackageAssignmentsCsv: (
      eventId: string,
      payload: { raceId: string; csvBase64?: string; csvText?: string; fileName?: string; contentType?: string },
    ) => post(`/events/${e(eventId)}/device-package-assignments`, payload),

    getDevicePackageAssignmentsMeta: (eventId: string, options: { raceId: string }) =>
      get(`/events/${e(eventId)}/device-package-assignments`, { raceId: options.raceId }),

    getDevicePackageAssignmentsDownloadUrl: (
      eventId: string,
      options: { raceId: string; expiresIn?: number },
    ) =>
      get(
        `/events/${e(eventId)}/device-package-assignments/download`,
        options as Record<string, string | number>,
      ),

    getDevicePackageAssignmentsCsv: (
      eventId: string,
      options: { raceId: string; expiresIn?: number },
    ) =>
      fetchCsvViaDownloadUrl(
        `/events/${e(eventId)}/device-package-assignments/download`,
        options,
        'device-package-assignments.csv',
      ),

    getRaceConfiguration: (params: { eventId: string; raceId: string }) =>
      get(`/events/${e(params.eventId)}/races/${e(params.raceId)}/race-configuration`),

    submitRaceConfiguration: (eventId: string, payload: Record<string, unknown>) => {
      const { raceId, ...rest } = payload as { raceId: string } & Record<string, unknown>;
      return post(`/events/${e(eventId)}/races/${e(raceId)}/race-configuration`, rest);
    },

    getRaceReportingConfiguration: (params: { eventId: string; raceId: string }) =>
      get(
        `/events/${e(params.eventId)}/races/${e(params.raceId)}/race-reporting-configuration`,
      ),

    submitRaceReportingConfiguration: (eventId: string, payload: Record<string, unknown>) => {
      const { raceId, ...rest } = payload as { raceId: string } & Record<string, unknown>;
      return post(
        `/events/${e(eventId)}/races/${e(raceId)}/race-reporting-configuration`,
        rest,
      );
    },

    createRaceReportingRun: (
      eventId: string,
      raceId: string,
      options: { excludedDeviceNums?: string[] } = {},
    ) => post(`/events/${e(eventId)}/races/${e(raceId)}/reporting/runs`, options),

    listRaceReportingRuns: (
      eventId: string,
      raceId: string,
      options: { limit?: number } = {},
    ) =>
      get(`/events/${e(eventId)}/races/${e(raceId)}/reporting/runs`, options as Record<string, number>),

    getRaceReportingRun: (eventId: string, raceId: string, runId: string) =>
      get(`/events/${e(eventId)}/races/${e(raceId)}/reporting/runs/${e(runId)}`),

    getRaceReportingRunArtifactDownloadUrl: (
      eventId: string,
      raceId: string,
      runId: string,
      artifactId: string,
      options: { expiresIn?: number } = {},
    ) =>
      get(
        `/events/${e(eventId)}/races/${e(raceId)}/reporting/runs/${e(runId)}/artifacts/${e(artifactId)}/download`,
        options as Record<string, number>,
      ),

    getRaceReportingRunLogs: (
      eventId: string,
      raceId: string,
      runId: string,
      options: { nextToken?: string | null; limit?: number } = {},
    ) =>
      get(
        `/events/${e(eventId)}/races/${e(raceId)}/reporting/runs/${e(runId)}/logs`,
        { nextToken: options.nextToken ?? undefined, limit: options.limit } as Record<string, string | number>,
      ),

    getRaceReportingAthleteCoverage: (eventId: string, raceId: string) =>
      get(`/events/${e(eventId)}/races/${e(raceId)}/reporting/athlete-coverage`),

    deleteRaceReportingRun: (eventId: string, raceId: string, runId: string) =>
      del(`/events/${e(eventId)}/races/${e(raceId)}/reporting/runs/${e(runId)}`),

    getLambdaLogs: (params: {
      functionName: string;
      filterPattern?: string;
      startTimeMs?: number;
      endTimeMs?: number;
      nextToken?: string | null;
      limit?: number;
    }) =>
      get('/lambda/logs', {
        functionName: params.functionName,
        filterPattern: params.filterPattern,
        startTimeMs: params.startTimeMs,
        endTimeMs: params.endTimeMs,
        nextToken: params.nextToken ?? undefined,
        limit: params.limit,
      } as Record<string, string | number>),

    getSystemHealth: () => get('/system/health'),

    listEventRaces: (eventId: string) => get(`/events/${e(eventId)}/races`),

    createEventRace: (eventId: string, payload: Record<string, unknown> = {}) =>
      post(`/events/${e(eventId)}/races`, payload),

    getEventRace: (eventId: string, raceId: string) =>
      get(`/events/${e(eventId)}/races/${e(raceId)}`),

    updateRaceStatus: (
      eventId: string,
      raceId: string,
      status?: string,
      options?: { retrievedDeviceIds?: string[] },
    ) =>
      post(`/events/${e(eventId)}/races/${e(raceId)}/status`, {
        status,
        retrievedDeviceIds: options?.retrievedDeviceIds,
      }),

    setRaceDevicesRacked: (
      eventId: string,
      raceId: string,
      payload: { deviceIds: string[]; racked?: 0 | 1 },
    ) => post(`/events/${e(eventId)}/races/${e(raceId)}/racked`, payload),

    setRaceDevicesDistributed: (
      eventId: string,
      raceId: string,
      payload: { deviceIds: string[]; distributed?: 0 | 1 },
    ) => post(`/events/${e(eventId)}/races/${e(raceId)}/distributed`, payload),

    releaseRaceDeviceLocks: (eventId: string, raceId: string, deviceIds?: string[]) =>
      post(`/events/${e(eventId)}/races/${e(raceId)}/device-locks/release`, { deviceIds }),

    deleteEventRace: (eventId: string, raceId: string, options?: { hard?: boolean }) =>
      del(`/events/${e(eventId)}/races/${e(raceId)}`, options as Record<string, boolean>),

    listEventOperators: (eventId: string) => get(`/events/${e(eventId)}/operators`),

    listUsers: () => get('/users'),

    createUser: (payload: Record<string, unknown>) => post('/users', payload),

    disableUser: (userId: string) =>
      del(`/users/${e(userId)}`),

    deprecateFirmware: (role: string, fileName: string) =>
      del('/firmware/versions', { role, fileName }),

    deleteEvent: (eventId: string, options: { hard?: boolean } = {}) =>
      del(`/events/${e(eventId)}`, options as Record<string, boolean>),

    removeEventOperators: (eventId: string, userIds: string[]) =>
      post(`/events/${e(eventId)}/operators/remove`, { userIds }),

    removeEventOperator: (eventId: string, userId: string) =>
      post(`/events/${e(eventId)}/operators/remove`, { userIds: [userId] }),

    removeEventDevices: (eventId: string, deviceIds: string[]) =>
      post(`/events/${e(eventId)}/devices/remove`, { deviceIds }),

    removeEventDevice: (eventId: string, deviceId: string) =>
      post(`/events/${e(eventId)}/devices/remove`, { deviceIds: [deviceId] }),

    setEventDeviceRecalled: (
      eventId: string,
      deviceId: string,
      recalled: 0 | 1,
      options: { raceId?: string } = {},
    ) =>
      post(`/events/${e(eventId)}/devices/${e(deviceId)}/recalled`, {
        recalled,
        raceId: options.raceId,
      }),

    setEventDeviceLostAndRetrieved: async (params: {
      eventId: string;
      raceId?: string;
      deviceId: string;
      reason?: string;
    }) => {
      const { eventId, deviceId, raceId, reason } = params;
      if (!eventId || !deviceId) return { ok: false, error: 'eventId_and_deviceId_required' };
      const normReason = reason?.trim() || 'Marked lost during race retrieval';
      const lifecycleRes = await post('/devices/lifecycle', {
        deviceId, eventId, raceId: raceId || undefined,
        lost: true, decommissioned: false, damaged: false, errored: false,
        reason: normReason,
      }) as { ok: boolean };
      if (!lifecycleRes.ok) return lifecycleRes;
      return post(
        `/events/${e(eventId)}/devices/${e(deviceId)}/recalled`,
        { recalled: 1, raceId: raceId || undefined },
      );
    },

    addDeviceNotes: (params: {
      deviceIds: string[];
      type: string;
      message: string;
      eventId?: string;
    }) => post('/notes/devices', params),

    listDeviceNotes: (params: { deviceId?: string; limit?: number; nextToken?: string }) =>
      get('/notes/devices', params as Record<string, string | number>),

    deleteDeviceNote: (params: { deviceId: string; noteId: string }) =>
      del(`/notes/devices/${e(params.deviceId)}/${e(params.noteId)}`),
  };
}
